import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import api from "@/api";
import { Creator, HomeContent, TINT_HEX } from "@/types";
import { OSChrome } from "@/components/os/OSChrome";

const CreatorCard = ({ c, i, onOpen }: { c: Creator; i: number; onOpen: () => void }) => (
  <motion.button
    data-testid={`creator-card-${c.slug}`}
    onClick={onOpen}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="group border-[3px] border-ink bg-white text-left shadow-hard transition-transform duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5"
  >
    <div className="flex items-center justify-between border-b-2 border-ink bg-lnpink px-2.5 py-1.5 font-spacemono text-[10px] font-bold uppercase tracking-wider">
      <span>{c.slug}.mov</span>
      <span aria-hidden>□ ×</span>
    </div>
    <div
      className="relative aspect-video"
      style={{ background: `linear-gradient(135deg, ${TINT_HEX[c.tint] || "#D9F7E7"} 0%, #ffffff 130%)` }}
    >
      <span className="scanlines absolute inset-0" aria-hidden />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-ink bg-white transition-transform duration-150 group-hover:scale-110">
          <Play size={20} className="ml-1 fill-ink text-ink" />
        </span>
      </span>
      <span className="bevel-in absolute inset-x-2.5 bottom-2.5 h-2 bg-white" aria-hidden>
        <span className="block h-full w-1/3 bg-ink" />
      </span>
    </div>
    <div className="p-3.5">
      <p className="font-pixel text-[11px] leading-relaxed">{c.name.toUpperCase()}</p>
      <p className="mt-1.5 text-xs font-medium text-ink/70">{c.niche}</p>
      <span className="mt-2.5 inline-block border-2 border-ink bg-butter px-1.5 py-0.5 font-spacemono text-[9px] font-bold tracking-wide">
        {c.metric}
      </span>
    </div>
  </motion.button>
);

export default function PortfolioPage() {
  const nav = useNavigate();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [content, setContent] = useState<HomeContent | null>(null);

  useEffect(() => {
    api.get("/creators").then((r) => setCreators(r.data)).catch(() => {});
    api.get("/content/home").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <OSChrome id="portfolio" title="portfolio.exe — Selected Client Work" tint="mint">
      <div className="p-5 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 data-testid="portfolio-heading" className="font-pixel text-lg leading-relaxed sm:text-2xl">
            THE<span className="ml-[0.35em]">LINEUP</span>
          </h2>
          {content?.status_published && (
            <div data-testid="status-tag" className="inline-flex rotate-[1.5deg] items-center gap-2 border-2 border-ink bg-butter px-3 py-1.5 shadow-hard-sm">
              <span className="status-dot h-2 w-2 bg-ink" aria-hidden />
              <span className="font-spacemono text-[10px] font-bold tracking-widest">STATUS: {content.status_text}</span>
            </div>
          )}
        </div>
        <p className="mt-3 max-w-lg text-sm font-medium text-ink/75">
          Every channel below trusted us with the remote. Pick a tape and press play!
        </p>

        <div className="mt-7 grid gap-5 sm:grid-cols-2">
          {creators.map((c, i) => (
            <CreatorCard key={c.id} c={c} i={i} onOpen={() => nav(`/portfolio/${c.slug}`)} />
          ))}
          {creators.length === 0 && (
            <p className="font-spacemono text-xs text-ink/60">Loading roster…</p>
          )}
        </div>

        <div className="mt-9 flex justify-center">
          <button data-testid="portfolio-cta" onClick={() => nav("/inquire")} className="btn-chunky bg-lnpink px-6 py-3 text-xs sm:text-sm">
            JOIN THE CREW <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </OSChrome>
  );
}
